"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const immutable_1 = require("immutable");
const LogManager_1 = require("../../Log/LogManager");
const ExponentialBackoffHelper_1 = require("../../Common/ExponentialBackoffHelper");
const TypeScriptUtils_1 = require("../../Common/TypeScriptUtils");
const ChatMessageAttachmentStatus_1 = require("../ChatMessageAttachmentStatus");
const ChatMessageDefinition_1 = require("../ChatMessageDefinition");
class AbstractChatMessageAttachmentService //: IDisposable
 {
    constructor(attachmentStore) {
        this._logger = LogManager_1.logManager.GetLogger("AbstractChatMessageAttachmentService");
        this._attachmentTaskQueue = [];
        this._isExecutingAttachmentTask = false;
        this._attachmentStore = attachmentStore;
    }
    async InitAsync() {
        await this._attachmentStore.InitAsync();
        //No need to do FixAllAttachmentStatus with any kind of db in Web/Electron.
        //await this.FixAllAttachmentStatus(this._attachmentStore);
        this._checkingTaskTimer = setInterval(async () => {
            //this._logger.info(`timer start ${this._checkingTaskTimer}`);
            await this.ExecuteAttachmentTaskAsync();
            //this._logger.info(`timer end ${this._checkingTaskTimer}`);
        }, 1000);
    }
    async TerminateAsync() {
        //TODO: Not sure when to clear timer.
        clearInterval(this._checkingTaskTimer);
        await this.ClearAsync();
    }
    async ClearAsync() {
        this._attachmentTaskQueue.splice(0);
        await this._attachmentStore.ClearAsync();
    }
    async UploadAttachment(cm, errorCallback, lastTryCount = 0) {
        const cmAttachmentService = this;
        if (!cmAttachmentService.IsSupportedContentType(cm.ContentType)) {
            if (errorCallback)
                errorCallback(`Not supported content type ${cm.ContentType}`);
            return;
        }
        // Check is running...
        if (cmAttachmentService.IsAttachmentTaskInQueue(cm)) {
            cmAttachmentService._logger.debug(`${cm.AttachmentName()} is in queue, no need to add task.`);
            return;
        }
        if (cm.IsContentTypeWithThumbnail() && await cm.HasThumbnailAsync()
            && !(await DoesChatMessageThumbnailIsSent(cm))) {
            //有Thumbnail就上傳Thumbnail
            await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.ThumbnailName(), ChatMessageAttachmentStatus_1.Status.PrepareSend, 0, 0);
        }
        if (cm.IsContentTypeWithAttachment() && (await cm.HasAttachmentAsync())
            && !(await DoesChatMessageAttachmntIsSent(cm))) {
            await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.AttachmentName(), ChatMessageAttachmentStatus_1.Status.PrepareSend, 0, 0);
        }
        cmAttachmentService._logger.debug(`${cm.AttachmentName()} is valid to upload, processing uploading...`);
        const uploadPromise = async () => {
            if (cm.IsContentTypeWithThumbnail() && (await cm.HasThumbnailAsync())
                && !(await DoesChatMessageThumbnailIsSent(cm))) {
                await cmAttachmentService.InternalUploadAttachment(cm, cm.ThumbnailName(), await cm.GetThumbnailAsync(), errorCallback);
                await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.ThumbnailName(), ChatMessageAttachmentStatus_1.Status.Sent, 0, 0); //成功更新狀態
                return; //先上傳Thumbnail跳出後再上傳Attachment，加速使用者端顯示
            }
            if (cm.IsContentTypeWithAttachment() && (await cm.HasAttachmentAsync())
                && !(await DoesChatMessageAttachmntIsSent(cm))) {
                await cmAttachmentService.InternalUploadAttachment(cm, cm.AttachmentName(), await cm.GetAttachmentAsync(), errorCallback);
                await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.AttachmentName(), ChatMessageAttachmentStatus_1.Status.Sent, 0, 0); //成功更新狀態
            }
        };
        const afterUploadPromise = async () => {
            if (await DoesChatMessageThumbnailAndAttachmentIsSent(cm)) {
                //都傳出去了，代表上傳成功
                cmAttachmentService._logger.debug(`Upload attachment=${cm.AttachmentName()} succeeded.`);
            }
            else {
                const hasRetry = await cmAttachmentService.RetryChatMessageAttachmentTask(cm, errorCallback, lastTryCount, (cm, errorCallback, lastTryCount) => cmAttachmentService.UploadAttachment(cm, errorCallback, lastTryCount));
                if (hasRetry) {
                    //還有下一次不必進一步判斷要不要結束
                    cmAttachmentService._logger.debug(`Retry upload attachment=${cm.AttachmentName()}`);
                }
                else {
                    //沒辦法Retry的話就直接標注失敗
                    if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsSent(cm))) {
                        //有Thumbnail的訊息才更新SendingStatus
                        await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.ThumbnailName(), ChatMessageAttachmentStatus_1.Status.SendingFailed, 0, 0);
                    }
                    if (!(await DoesChatMessageAttachmntIsSent(cm))) {
                        await cmAttachmentService.UpdateAttachmentSendingStatusAsync(cm.AttachmentName(), ChatMessageAttachmentStatus_1.Status.SendingFailed, 0, 0);
                    }
                }
            }
        };
        cmAttachmentService.EnqueueAttachmentTask(new TypeScriptUtils_1.Tuple3({
            Item1: cm,
            Item2: uploadPromise,
            Item3: afterUploadPromise
        }));
        async function DoesChatMessageThumbnailAndAttachmentIsSent(localCm) {
            if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsSent(localCm)))
                return false;
            if (!(await DoesChatMessageAttachmntIsSent(localCm)))
                return false;
            return true;
        }
        async function DoesChatMessageThumbnailIsSent(localCm) {
            const thumbnailStatus = await cmAttachmentService.QueryAttachmentStatusAsync(localCm.ThumbnailName());
            return thumbnailStatus.Status === ChatMessageAttachmentStatus_1.Status.Sent;
        }
        async function DoesChatMessageAttachmntIsSent(localCm) {
            const attachmentStatus = await cmAttachmentService.QueryAttachmentStatusAsync(localCm.AttachmentName());
            return attachmentStatus.Status === ChatMessageAttachmentStatus_1.Status.Sent;
        }
    }
    async DownloadAttachment(cm, errorCallback, lastTryCount = 0) {
        const cmAttachmentService = this;
        if (!cmAttachmentService.IsSupportedContentType(cm.ContentType)) {
            cmAttachmentService._logger.warn(`Not supported content type ${cm.ContentType}`);
            return;
        }
        // Check attachment exist
        if ((await DoesChatMessageThumbnailAndAttachmentIsReceived(cm))) {
            cmAttachmentService._logger.debug(`ChatMessage=${cm.id} all attachment exist, no need to download.`);
            return;
        }
        // Check is downloading...
        if (cmAttachmentService.IsAttachmentTaskInQueue(cm)) {
            cmAttachmentService._logger.debug(`${cm.AttachmentName()} is in queue, no need to add task.`);
            return;
        }
        if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsReceived(cm))) {
            const thumbnailName = cm.ThumbnailName();
            await cmAttachmentService.UpdateAttachmentReceivingStatusAsync(thumbnailName, ChatMessageAttachmentStatus_1.Status.PrepareReceive);
        }
        if (cm.IsContentTypeWithAttachment() && !(await DoesChatMessageAttachmentIsReceived(cm))) {
            const attachmentName = cm.AttachmentName();
            await cmAttachmentService.UpdateAttachmentReceivingStatusAsync(attachmentName, ChatMessageAttachmentStatus_1.Status.PrepareReceive);
        }
        cmAttachmentService._logger.debug(`${cm.AttachmentName()} not exist, processing downloading...`);
        const downloadPromise = async () => {
            cmAttachmentService._logger.debug(`${lastTryCount} time to try to download=${cm.AttachmentName()}`);
            //if (this.IsFileTaskRunning(), $"There is another file tasking running.");
            if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsReceived(cm))) {
                //有Thumbnail就下載Thumbnail
                await cmAttachmentService.DownloadAttachmentStart(cm, cm.ThumbnailName());
                return; //先下載Thumbnail跳出後再下載Attachment，加速使用者端顯示
            }
            if (cm.IsContentTypeWithAttachment() && !(await DoesChatMessageAttachmentIsReceived(cm))) {
                //有Attachment就下載Attachment
                await cmAttachmentService.DownloadAttachmentStart(cm, cm.AttachmentName());
            }
        };
        const afterDownloadPromise = async () => {
            if ((await DoesChatMessageThumbnailAndAttachmentIsReceived(cm))) {
                cmAttachmentService._logger.debug(`Download attachment=${cm.AttachmentName()} succeeded.`);
            }
            else {
                const hasRetry = await cmAttachmentService.RetryChatMessageAttachmentTask(cm, errorCallback, lastTryCount, (cm, errorCallback, lastTryCount) => cmAttachmentService.DownloadAttachment(cm, errorCallback, lastTryCount));
                if (hasRetry) {
                    //還有下一次不必進一步判斷要不要結束
                }
                else {
                    //沒辦法Retry的話就直接標注失敗
                    if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsReceived(cm))) {
                        //有Thumbnail的訊息才更新SendingStatus
                        await cmAttachmentService.UpdateAttachmentReceivingStatusAsync(cm.ThumbnailName(), ChatMessageAttachmentStatus_1.Status.RecevingFailed);
                    }
                    if (!(await DoesChatMessageAttachmentIsReceived(cm))) {
                        await cmAttachmentService.UpdateAttachmentReceivingStatusAsync(cm.AttachmentName(), ChatMessageAttachmentStatus_1.Status.RecevingFailed);
                    }
                }
            }
        };
        cmAttachmentService.EnqueueAttachmentTask(new TypeScriptUtils_1.Tuple3({
            Item1: cm,
            Item2: downloadPromise,
            Item3: afterDownloadPromise
        }));
        async function DoesChatMessageThumbnailAndAttachmentIsReceived(localCm) {
            if (cm.IsContentTypeWithThumbnail() && !(await DoesChatMessageThumbnailIsReceived(localCm)))
                return false;
            if (!(await DoesChatMessageAttachmentIsReceived(localCm)))
                return false;
            return true;
        }
        async function DoesChatMessageThumbnailIsReceived(localCm) {
            const thumbnailStatus = await cmAttachmentService.QueryAttachmentStatusAsync(localCm.ThumbnailName());
            return thumbnailStatus.Status === ChatMessageAttachmentStatus_1.Status.Received
                || thumbnailStatus.IsInStorage;
        }
        async function DoesChatMessageAttachmentIsReceived(localCm) {
            const attachmentStatus = await cmAttachmentService.QueryAttachmentStatusAsync(localCm.AttachmentName());
            return attachmentStatus.Status === ChatMessageAttachmentStatus_1.Status.Received
                || attachmentStatus.IsInStorage;
        }
    }
    async RetryChatMessageAttachmentTask(cm, errorCallback, lastTryCount, attachmentTask) {
        const MAX_TRY_COUNT = 10;
        const tryCount = lastTryCount++;
        const attachmentName = cm.AttachmentName();
        if (tryCount < MAX_TRY_COUNT) {
            //第一次重試不等待馬上衝，因為每個Task只要是圖片都要上傳or下載兩次一次Thumbnail一次Attachment
            if (tryCount > 1) {
                const delayTimeMs = ExponentialBackoffHelper_1.NextDelayTimeMsFromExponentialBackoff(tryCount - 1);
                this._logger.debug(`Wait ${tryCount}=${delayTimeMs}ms for ${attachmentName}`);
                await TypeScriptUtils_1.delay(delayTimeMs);
            }
            //這次沒完成，產新的Task推回Queue裡面，讓其他可完成的Task有機會執行。
            await attachmentTask(cm, errorCallback, lastTryCount);
            return true;
        }
        else {
            this._logger.error(`Try to download attachment=${attachmentName}, ${lastTryCount} times, drop this attachmet.`);
            return false;
        }
    }
    async DownloadAttachmentStart(cm, attachmentName) {
        this._logger.debug(`Prepare to query attachment ${attachmentName}`);
        const cmaStatus = await this.QueryRemoteAttachmentStatus(cm, attachmentName);
        switch (cmaStatus) {
            case ChatMessageAttachmentStatus_1.Status.NotExist:
            case ChatMessageAttachmentStatus_1.Status.RecevingFailed:
                // 檔案不存在也等不到
                await this.UpdateAttachmentReceivingStatusAsync(attachmentName, ChatMessageAttachmentStatus_1.Status.RecevingFailed);
                return;
            case ChatMessageAttachmentStatus_1.Status.None:
            case ChatMessageAttachmentStatus_1.Status.Receiving:
                // 對方檔案還在收，等下一輪
                this._logger.debug(`Server is receiving ${attachmentName}, waiting for next time.`);
                break;
            case ChatMessageAttachmentStatus_1.Status.Received:
                await this.InternalDownloadAttachment(cm, attachmentName, null);
                await this.UpdateAttachmentReceivingStatusAsync(attachmentName, ChatMessageAttachmentStatus_1.Status.Received);
                break;
        }
    }
    async ReportNewAttachmentStatusAsync(attachmentName) {
        const curStatus = await this.QueryAttachmentStatusAsync(attachmentName);
        if (curStatus === ChatMessageAttachmentStatus_1.ChatMessageAttachmentStatus.INVALID
            || curStatus.Status === ChatMessageAttachmentStatus_1.Status.None) {
            // 表示Attachment不存在，先加一筆記錄，讓Client來要檔案時問的到記錄
            await this._attachmentStore.UpdateAttachmentReceivingStatusAsync(attachmentName, ChatMessageAttachmentStatus_1.Status.Receiving);
        }
    }
    async QueryAttachmentStatusAsync(attachmentName) {
        const cmas = await this._attachmentStore.QueryAttachmentStatusAsync(attachmentName);
        //return this.ProcessChatMessageAttachmentStatus(attachmentName, cmas);
        return cmas;
    }
    // private ProcessChatMessageAttachmentStatus(attachmentName: string, cmas: ChatMessageAttachmentStatus): ChatMessageAttachmentStatus {
    //   return retCmas;
    //   const retCmas = cmas;
    //   switch (cmas.Status) {
    //     case Status.Received:
    //       if (!this._attachmentStore.IsInStorage(attachmentName)) {
    //         retCmas = retCmas.ApplyReceivingStatus(Status.NotExist);
    //       }
    //       break;
    //     case Status.None:
    //       if (this._attachmentStore.IsInStorage(attachmentName)) {
    //         retCmas = retCmas.ApplyReceivingStatus(Status.Received);
    //       }
    //       else {
    //         retCmas = retCmas.ApplyReceivingStatus(Status.None);
    //       }
    //       break;
    //   }
    //   return retCmas;
    // }
    QueryAttachmentFilePathName(cm) {
        if (cm.IsImage() || cm.IsAudio()) {
            return this._attachmentStore.QueryAttachmentFilePathName(cm.AttachmentName());
        }
        else {
            return "";
        }
    }
    async RetrieveAttachmentBytesAsync(attachmentName) {
        return await this._attachmentStore.LoadAsync(attachmentName);
    }
    PersistAttachmentStatusToStorage() {
        this._attachmentStore.PersistToStorage();
    }
    async FixAllAttachmentStatus(attachmentStore) {
        const allAttachmentStatus = await attachmentStore.QueryAllAttachmentStatusAsync();
        for (const cmas of allAttachmentStatus) {
            switch (cmas.Status) {
                case ChatMessageAttachmentStatus_1.Status.PrepareReceive:
                case ChatMessageAttachmentStatus_1.Status.Receiving:
                    this._logger.warn(`Adjust ${cmas.Name} status from ${ChatMessageAttachmentStatus_1.Status.Receiving} to ${ChatMessageAttachmentStatus_1.Status.RecevingFailed}`);
                    await attachmentStore.UpdateAttachmentReceivingStatusAsync(cmas.Name, ChatMessageAttachmentStatus_1.Status.RecevingFailed);
                    break;
                case ChatMessageAttachmentStatus_1.Status.Received:
                    if (!(await attachmentStore.IsInStorageAsync(cmas.Name))) {
                        this._logger.warn(`Adjust ${cmas.Name} status from ${ChatMessageAttachmentStatus_1.Status.Received} to ${ChatMessageAttachmentStatus_1.Status.NotExist}`);
                        await attachmentStore.UpdateAttachmentReceivingStatusAsync(cmas.Name, ChatMessageAttachmentStatus_1.Status.NotExist);
                    }
                    break;
            }
            switch (cmas.Status) {
                case ChatMessageAttachmentStatus_1.Status.PrepareSend:
                case ChatMessageAttachmentStatus_1.Status.Sending:
                    await attachmentStore.UpdateAttachmentSendingStatsAsync(cmas.Name, ChatMessageAttachmentStatus_1.Status.SendingFailed, 0, 0);
                    break;
            }
        }
    }
    async ShowAllAttachmentStatusAsync() {
        const allStatus = await this._attachmentStore.QueryAllAttachmentStatusAsync();
        for (const s of allStatus) {
            this._logger.debug(`${s}`);
        }
    }
    async UpdateAttachmentReceivingStatusAsync(name, status) {
        await this._attachmentStore.UpdateAttachmentReceivingStatusAsync(name, status);
    }
    async UpdateAttachmentReceivingStatsAsync(name, cur, total) {
        await this._attachmentStore.UpdateAttachmentReceivingStatsAsync(name, cur, total);
    }
    async UpdateAttachmentSendingStatusAsync(name, status, cur, total) {
        await this._attachmentStore.UpdateAttachmentSendingStatsAsync(name, status, cur, total);
    }
    async HasAttachmentAsync(name) {
        return await this.AttachmentIsInStorageAsync(name);
    }
    async SaveAttachmentAsync(name, attachmentBytes) {
        await this._attachmentStore.SaveAsync(name, attachmentBytes);
    }
    async SaveAttachmentFromStreamAsync(name, incomingStream, streamLength) {
        await this._attachmentStore.SaveFromStreamAsync(name, incomingStream, streamLength);
    }
    async LoadAttachmentAsync(name) {
        return this._attachmentStore.LoadAsync(name);
    }
    async DeleteAttachmentAsync(name) {
        return this._attachmentStore.DeleteAsync(name);
    }
    async AttachmentIsInStorageAsync(name) {
        return await this._attachmentStore.IsInStorageAsync(name);
    }
    IsSupportedContentType(contentType) {
        return AbstractChatMessageAttachmentService.SupportedContentTypes.includes(contentType);
    }
    EnqueueAttachmentTask(attachmentTask) {
        this._attachmentTaskQueue.push(attachmentTask);
        this._logger.debug(`Enqueue ${attachmentTask.Item1.AttachmentName()} task, there are ${this._attachmentTaskQueue.length} tasks in queue.`);
    }
    async ExecuteAttachmentTaskAsync() {
        this._isExecutingAttachmentTask = true;
        let afterTask = null;
        let currentTaskName = "";
        try {
            if (this.IsFileTaskRunning())
                return;
            if (this._attachmentTaskQueue.length === 0)
                return;
            const cmAndTask = this._attachmentTaskQueue[0]; //execute first task
            if (!cmAndTask) {
                this._logger.warn("Try to peeking queue but failed, wait next time.");
                return;
            }
            const cm = cmAndTask.Item1;
            const task = cmAndTask.Item2;
            afterTask = cmAndTask.Item3;
            currentTaskName = cm.AttachmentName();
            this._logger.debug(`Start to run task=${currentTaskName}, there are ${this._attachmentTaskQueue.length} task in queue.`);
            await task();
            this._logger.debug(`End of run task=${currentTaskName}`);
        }
        catch (error) {
            this._logger.error(`Failure of run task=${currentTaskName}, reason=${error.message}`, error);
        }
        finally {
            this._attachmentTaskQueue.shift(); //remove first task
            try {
                if (afterTask)
                    await afterTask();
            }
            catch (error) {
                this._logger.error(`Failure of run after task=${currentTaskName}, reason=${error.message}`, error);
            }
        }
        this._isExecutingAttachmentTask = false;
    }
    IsAttachmentTaskInQueue(cm) {
        if (this._attachmentTaskQueue.findIndex((nametask) => cm === nametask.Item1) !== -1) {
            //this._logger.debug(`Incoming task=${cm.AttachmentName()} is in task queue.`);
            return true;
        }
        return false;
    }
    CountOfAttachmentTask() {
        let count = 0;
        count += this._attachmentTaskQueue.length;
        return count;
    }
    IsExecutingAttachmentTask() {
        return this._isExecutingAttachmentTask || this._attachmentTaskQueue.length > 0;
    }
}
AbstractChatMessageAttachmentService.SupportedContentTypes = immutable_1.List([
    ChatMessageDefinition_1.ChatMessageDefinition.CONTENT_TYPE_IMG_JPEG,
    ChatMessageDefinition_1.ChatMessageDefinition.CONTENT_TYPE_IMG_PNG,
    ChatMessageDefinition_1.ChatMessageDefinition.CONTENT_TYPE_AUDIO_AAC,
    ChatMessageDefinition_1.ChatMessageDefinition.CONTENT_TYPE_AUDIO_WEBM,
]);
exports.AbstractChatMessageAttachmentService = AbstractChatMessageAttachmentService;
//# sourceMappingURL=AbstractChatMessageAttachmentService.js.map